import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MaskLine, Wordmark } from "../components/Motion";
import { UserRound, Copy, Check, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function Account() {
  const { user, ready, logout } = useAuth();
  const { setDiscount, setOpen } = useCart();
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!ready) {
    return <div className="min-h-[50vh] flex items-center justify-center font-mono text-graytx text-sm">Loading…</div>;
  }

  // Logged out — prompt to sign up / log in
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-24 sm:py-32 text-center" data-testid="account-page">
        <div className="inline-flex items-center justify-center w-16 h-16 border border-line mb-8">
          <UserRound size={26} className="text-graytx" />
        </div>
        <h1 className="font-display font-extrabold text-5xl sm:text-7xl uppercase leading-[0.85]"><MaskLine>Account</MaskLine></h1>
        <p className="text-graytx mt-6 max-w-md mx-auto leading-relaxed">
          Create an account to unlock your <span className="text-cream">15% off code</span>, early access to drops, and pre-order tracking.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
          <Link to="/signup" data-testid="account-signup-link" className="bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 hover:bg-cs-yellow transition-colors duration-200">Create Account & Get 15%</Link>
          <Link to="/login" data-testid="account-login-link" className="border border-line font-mono text-[12px] uppercase tracking-[0.2em] px-8 py-4 hover:bg-charcoal transition-colors duration-200">Log In</Link>
        </div>
      </div>
    );
  }

  // Logged in
  const copyCode = () => {
    navigator.clipboard.writeText(user.discount_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const useAtCheckout = () => {
    setDiscount(user.discount_code);
    toast.success("15% code applied — opening your bag");
    setOpen(true);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28" data-testid="account-dashboard">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-graytx mb-4">Your account</p>
      <h1 className="font-display font-extrabold text-5xl sm:text-6xl uppercase leading-[0.85]">
        <MaskLine>Hey, {user.name?.split(" ")[0] || "friend"}</MaskLine>
      </h1>
      <p className="text-graytx mt-4 font-mono text-sm" data-testid="account-email">{user.email}</p>

      <div className="mt-10 border border-line p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cs-yellow">Your 15% off code</p>
        <button onClick={copyCode} data-testid="account-copy-code"
          className="mt-4 w-full flex items-center justify-between border-2 border-dashed border-cs-yellow px-5 py-4">
          <span className="font-mono text-lg tracking-[0.15em]" data-testid="account-discount-code">{user.discount_code}</span>
          {copied ? <Check size={18} className="text-cs-green" /> : <Copy size={16} className="text-graytx" />}
        </button>
        <button onClick={useAtCheckout} data-testid="account-use-code"
          className="mt-4 w-full bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] py-3.5 hover:bg-cs-yellow transition-colors duration-200">
          Apply to my bag
        </button>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={() => { logout(); nav("/"); }} data-testid="account-logout"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graytx hover:text-cs-red transition-colors duration-200">
          <LogOut size={14} /> Log out
        </button>
        <Wordmark className="text-xl" />
      </div>
    </div>
  );
}
